export const OcppVersion = {
  V16J: '1.6J',
  V201: '2.0.1',
  V21: '2.1',
} as const;
export type OcppVersion = (typeof OcppVersion)[keyof typeof OcppVersion];

export const ConnectorStatus = {
  Available: 'Available',
  Preparing: 'Preparing',
  Charging: 'Charging',
  SuspendedEV: 'SuspendedEV',
  SuspendedEVSE: 'SuspendedEVSE',
  Finishing: 'Finishing',
  Reserved: 'Reserved',
  Unavailable: 'Unavailable',
  Faulted: 'Faulted',
} as const;
export type ConnectorStatus = (typeof ConnectorStatus)[keyof typeof ConnectorStatus];

export const Availability = {
  Operative: 'Operative',
  Inoperative: 'Inoperative',
} as const;
export type Availability = (typeof Availability)[keyof typeof Availability];

export const AuthStatus = {
  Accepted: 'Accepted',
  Blocked: 'Blocked',
  Expired: 'Expired',
  Invalid: 'Invalid',
  ConcurrentTx: 'ConcurrentTx',
} as const;
export type AuthStatus = (typeof AuthStatus)[keyof typeof AuthStatus];

export const ProfilePurpose = {
  ChargePointMaxProfile: 'ChargePointMaxProfile',
  TxDefaultProfile: 'TxDefaultProfile',
  TxProfile: 'TxProfile',
} as const;
export type ProfilePurpose = (typeof ProfilePurpose)[keyof typeof ProfilePurpose];

export const ProfileKind = {
  Absolute: 'Absolute',
  Recurring: 'Recurring',
  Relative: 'Relative',
} as const;
export type ProfileKind = (typeof ProfileKind)[keyof typeof ProfileKind];

export const RecurrencyKind = {
  Daily: 'Daily',
  Weekly: 'Weekly',
} as const;
export type RecurrencyKind = (typeof RecurrencyKind)[keyof typeof RecurrencyKind];

export const ChargingRateUnit = {
  W: 'W',
  A: 'A',
} as const;
export type ChargingRateUnit = (typeof ChargingRateUnit)[keyof typeof ChargingRateUnit];

// Version-neutral superset of every OCPP stop/stopped reason. Not every
// value is legal on every wire version — `stopReasonsForVersion()` (see
// domain/stopReasons.ts) gates which subset a given OcppVersion may send,
// and the vendored Ajv schemas are the final backstop. Grouped by origin:
//   • shared 1.6 + 2.x
//   • 1.6-only (no 2.x equivalent — adapter folds them to the closest 2.x value)
//   • 2.0.1+ additions
//   • 2.1-only addition
export const StopReason = {
  // shared (1.6 + 2.0.1 + 2.1)
  EmergencyStop: 'EmergencyStop',
  EVDisconnected: 'EVDisconnected',
  Local: 'Local',
  Other: 'Other',
  PowerLoss: 'PowerLoss',
  Reboot: 'Reboot',
  Remote: 'Remote',
  DeAuthorized: 'DeAuthorized',
  // 1.6-only
  HardReset: 'HardReset',
  SoftReset: 'SoftReset',
  UnlockCommand: 'UnlockCommand',
  // 2.0.1+ additions
  EnergyLimitReached: 'EnergyLimitReached',
  GroundFault: 'GroundFault',
  ImmediateReset: 'ImmediateReset',
  LocalOutOfCredit: 'LocalOutOfCredit',
  MasterPass: 'MasterPass',
  OvercurrentFault: 'OvercurrentFault',
  PowerQuality: 'PowerQuality',
  SOCLimitReached: 'SOCLimitReached',
  StoppedByEV: 'StoppedByEV',
  TimeLimitReached: 'TimeLimitReached',
  Timeout: 'Timeout',
  // 2.1-only addition
  ReqEnergyTransferRejected: 'ReqEnergyTransferRejected',
} as const;
export type StopReason = (typeof StopReason)[keyof typeof StopReason];

export const ConnectionState = {
  Disconnected: 'Disconnected',
  Connecting: 'Connecting',
  Connected: 'Connected',
  Reconnecting: 'Reconnecting',
  Error: 'Error',
} as const;
export type ConnectionState = (typeof ConnectionState)[keyof typeof ConnectionState];

export const RecoveryState = {
  Live: 'live',
  Adopted: 'adopted',
  Orphaned: 'orphaned',
} as const;
export type RecoveryState = (typeof RecoveryState)[keyof typeof RecoveryState];

export const OfflineSessionStatus = {
  Queued: 'queued',
  Uploading: 'uploading',
  Uploaded: 'uploaded',
  Failed: 'failed',
} as const;
export type OfflineSessionStatus = (typeof OfflineSessionStatus)[keyof typeof OfflineSessionStatus];

export const ConfigurationKeyType = {
  Integer: 'integer',
  Boolean: 'boolean',
  String: 'string',
  CSL: 'CSL',
} as const;
export type ConfigurationKeyType = (typeof ConfigurationKeyType)[keyof typeof ConfigurationKeyType];

export const ConfigurationSection = {
  Core: 'Core',
  Auth: 'Auth',
  SmartCharging: 'SmartCharging',
  Custom: 'Custom',
} as const;
export type ConfigurationSection = (typeof ConfigurationSection)[keyof typeof ConfigurationSection];
